import React, { useState, useEffect } from 'react';
import './PublishModal.css';
import { buildApiUrl } from '../../config/api';

const PublishModal = ({ isOpen, onClose, onPublish, siteId }) => {
  const [subdomain, setSubdomain] = useState('');
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState(null); // null, 'available', 'taken', 'error'
  const [deployToCloudflare, setDeployToCloudflare] = useState(true);
  const [environment, setEnvironment] = useState('staging'); // 'staging', 'production'
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishStep, setPublishStep] = useState('configure'); // 'configure', 'publishing', 'success', 'error'
  const [publishResult, setPublishResult] = useState(null);
  const [customDomain, setCustomDomain] = useState('');
  const [useCustomDomain, setUseCustomDomain] = useState(false);
  const [isCheckingDomain, setIsCheckingDomain] = useState(false);
  const [domainValidation, setDomainValidation] = useState(null); // null, 'valid', 'invalid'
  const [existingDeployments, setExistingDeployments] = useState({ production: null, staging: null });
  const [isLoadingDeployments, setIsLoadingDeployments] = useState(false);

  // Fetch existing deployments when modal opens
  useEffect(() => {
    if (isOpen && siteId) {
      fetchExistingDeployments();
    }
  }, [isOpen, siteId]);

  const fetchExistingDeployments = async () => {
    setIsLoadingDeployments(true);
    try {
      const response = await fetch(buildApiUrl(`api/sites/${siteId}/deployments`));
      const data = await response.json();
      
      if (data.success) {
        setExistingDeployments(data.deployments);
        
        // If user has existing deployment for current environment, pre-fill subdomain
        const currentDeployment = data.deployments[environment];
        if (currentDeployment && !subdomain) {
          if (currentDeployment.isCustomDomain) {
            setUseCustomDomain(true);
            setCustomDomain(currentDeployment.customDomain);
          } else {
            setSubdomain(currentDeployment.subdomain);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching existing deployments:', error);
    } finally {
      setIsLoadingDeployments(false);
    }
  };

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSubdomain('');
      setAvailabilityStatus(null);
      setDeployToCloudflare(true);
      setEnvironment('staging');
      setIsPublishing(false);
      setPublishStep('configure');
      setPublishResult(null);
      setCustomDomain('');
      setUseCustomDomain(false);
      setDomainValidation(null);
    }
  }, [isOpen]);

  // Check subdomain availability with debouncing
  useEffect(() => {
    if (!subdomain || subdomain.length < 3 || useCustomDomain) {
      setAvailabilityStatus(null);
      return;
    }

    // Validate subdomain format
    const subdomainRegex = /^[a-z0-9-]+$/;
    if (!subdomainRegex.test(subdomain)) {
      setAvailabilityStatus('invalid');
      return;
    }

    const timeoutId = setTimeout(() => {
      checkSubdomainAvailability();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [subdomain, environment, useCustomDomain]);

  // Check custom domain validation with debouncing
  useEffect(() => {
    if (!customDomain || !useCustomDomain) {
      setDomainValidation(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      validateCustomDomain();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [customDomain, useCustomDomain]);

    const validateCustomDomain = async () => {    if (!customDomain) {      setDomainValidation(null);      return;    }    setIsCheckingDomain(true);    try {      const response = await fetch(buildApiUrl(`api/custom-domains/validate/${encodeURIComponent(customDomain)}`));      const data = await response.json();            if (response.ok && data.success) {        setDomainValidation(data.isValid ? 'valid' : 'invalid');      } else {        setDomainValidation('invalid');      }    } catch (error) {      console.error('Error validating custom domain:', error);      setDomainValidation('invalid');    } finally {      setIsCheckingDomain(false);    }  };

  const checkSubdomainAvailability = async () => {
    if (!subdomain) return;

    setIsCheckingAvailability(true);
    try {
      // Check if this subdomain belongs to current site's existing deployments
      const currentDeployment = existingDeployments[environment];
      const isOwnSubdomain = currentDeployment && currentDeployment.subdomain === subdomain;
      
      const response = await fetch(buildApiUrl(`api/subdomains/check/${subdomain}?environment=${environment}&allowExisting=${isOwnSubdomain}`));
      const data = await response.json();

      if (response.ok) {
        if (data.exists && isOwnSubdomain) {
          setAvailabilityStatus('existing'); // New status for existing owned subdomain
        } else {
          setAvailabilityStatus(data.available ? 'available' : 'taken');
        }
      } else {
        setAvailabilityStatus('error');
      }
    } catch (error) {
      console.error('Error checking subdomain availability:', error);
      setAvailabilityStatus('error');
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  const handleSubdomainChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSubdomain(value);
  };

  const handleCustomDomainChange = (e) => {
    const value = e.target.value.toLowerCase().trim();
    setCustomDomain(value);
  };

  const handlePublish = async () => {
    setIsPublishing(true);
    setPublishStep('publishing');

    try {
      const publishData = {
        deployToCloudflare: true,
        environment,
        subdomain: useCustomDomain ? null : subdomain,
        useCustomDomain,
        customDomain: useCustomDomain ? customDomain : null
      };

      const result = await onPublish(publishData);
      
      if (result && result.success) {
        setPublishResult(result);
        setPublishStep('success');
      } else {
        setPublishResult(result || { error: 'Unknown error occurred' });
        setPublishStep('error');
      }
    } catch (error) {
      console.error('Error publishing site:', error);
      setPublishResult({ error: error.message });
      setPublishStep('error');
    } finally {
      setIsPublishing(false);
    }
  };

  const canPublish = () => {
    if (useCustomDomain) {
      return customDomain && domainValidation === 'valid';
    } else {
      return subdomain && (availabilityStatus === 'available' || availabilityStatus === 'existing');
    }
  };

  const getAvailabilityIcon = () => {
    if (isCheckingAvailability) return <i className="fas fa-spinner fa-spin"></i>;
    
    switch (availabilityStatus) {
      case 'available':
        return <i className="fas fa-check-circle text-success"></i>;
      case 'existing':
        return <i className="fas fa-sync-alt text-info"></i>;
      case 'taken':
        return <i className="fas fa-times-circle text-error"></i>;
      case 'invalid':
        return <i className="fas fa-exclamation-triangle text-warning"></i>;
      case 'error':
        return <i className="fas fa-exclamation-circle text-error"></i>;
      default:
        return null;
    }
  };

  const getDomainValidationIcon = () => {
    if (isCheckingDomain) return <i className="fas fa-spinner fa-spin"></i>;
    
    switch (domainValidation) {
      case 'valid':
        return <i className="fas fa-check-circle text-success"></i>;
      case 'invalid':
        return <i className="fas fa-times-circle text-error"></i>;
      default:
        return null;
    }
  };

  const getAvailabilityMessage = () => {
    const fullDomain = environment === 'production' 
      ? `${subdomain}.twilf.site`
      : `${environment}-${subdomain}.twilf.site`;

    switch (availabilityStatus) {
      case 'available':
        return `${fullDomain} is available!`;
      case 'existing':
        return `${fullDomain} is your existing ${environment} site - you can redeploy to it`;
      case 'taken':
        return `${fullDomain} is already taken`;
      case 'invalid':
        return 'Only lowercase letters, numbers, and hyphens allowed';
      case 'error':
        return 'Error checking availability';
      default:
        return '';
    }
  };

  const getDomainValidationMessage = () => {
    switch (domainValidation) {
      case 'valid':
        return `${customDomain} is a valid domain format`;
      case 'invalid':
        return 'Please enter a valid domain (e.g., example.com)';
      default:
        return '';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="publish-modal-overlay">
      <div className="publish-modal">
        <div className="publish-modal-header">
          <h2>
            <i className="fas fa-globe"></i>
            Publish Your Website
          </h2>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="publish-modal-content">
          {publishStep === 'configure' && (
            <>
              {/* Existing Deployments Section */}
              <div className="existing-deployments-section">
                <h3>
                  <i className="fas fa-history"></i>
                  Your Existing Deployments
                </h3>

                {isLoadingDeployments ? (
                  <div className="deployments-loader">
                    <div className="loader-content">
                      <i className="fas fa-spinner fa-spin"></i>
                      <p>Getting your existing deployments...</p>
                    </div>
                  </div>
                ) : (existingDeployments.production || existingDeployments.staging) ? (
                  <div className="existing-deployments">
                    {existingDeployments.production && (
                      <div className="existing-deployment production">
                        <div className="deployment-info">
                          <div className="deployment-icon">
                            <i className="fas fa-globe"></i>
                          </div>
                          <div className="deployment-details">
                            <h4>Production</h4>
                            <p>{existingDeployments.production.url}</p>
                            <small>
                              Last deployed: {new Date(existingDeployments.production.lastDeployedAt).toLocaleDateString()}
                            </small>
                          </div>
                        </div>
                        <button
                          className="redeploy-btn"
                          onClick={() => {
                            setSubdomain(existingDeployments.production.subdomain);
                            setEnvironment('production');
                          }}
                        >
                          <i className="fas fa-sync-alt"></i>
                          Redeploy
                        </button>
                      </div>
                    )}

                    {existingDeployments.staging && (
                      <div className="existing-deployment staging">
                        <div className="deployment-info">
                          <div className="deployment-icon">
                            <i className="fas fa-flask"></i>
                          </div>
                          <div className="deployment-details">
                            <h4>Staging</h4>
                            <p>{existingDeployments.staging.url}</p>
                            <small>
                              Last deployed: {new Date(existingDeployments.staging.lastDeployedAt).toLocaleDateString()}
                            </small>
                          </div>
                        </div>
                        <button
                          className="redeploy-btn"
                          onClick={() => {
                            setSubdomain(existingDeployments.staging.subdomain);
                            setEnvironment('staging');
                          }}
                        >
                          <i className="fas fa-sync-alt"></i>
                          Redeploy
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="no-deployments">
                    <div className="no-deployments-content">
                      <i className="fas fa-rocket"></i>
                      <p>No existing deployments found</p>
                      <small>Create your first deployment below</small>
                    </div>
                  </div>
                )}
              </div>

              {/* Cloudflare Deployment Section */}
              <div className="cloudflare-deployment-section">
                <div className="cloudflare-options">
                  {/* Subdomain Configuration */}
                  <div className="subdomain-section">
                    <label htmlFor="subdomain">Choose your subdomain:</label>
                    <div className="subdomain-input-group">
                      <input
                        type="text"
                        id="subdomain"
                        value={subdomain}
                        onChange={handleSubdomainChange}
                        placeholder="mysite"
                        className={`subdomain-input ${availabilityStatus ? availabilityStatus : ''}`}
                      />
                      <span className="domain-suffix">.twilf.site</span>
                      <div className="availability-indicator">
                        {getAvailabilityIcon()}
                      </div>
                    </div>
                    {availabilityStatus && (
                      <div className={`availability-message ${availabilityStatus}`}>
                        {getAvailabilityMessage()}
                      </div>
                    )}
                    <div className="subdomain-help">
                      <small>
                        <i className="fas fa-info-circle"></i>
                        Minimum 3 characters. Only lowercase letters, numbers, and hyphens.
                      </small>
                    </div>
                  </div>

                  {/* Environment Selection */}
                  <div className="environment-section">
                    <label>Choose deployment environment:</label>
                    <div className="environment-options">
                      <div className="environment-option">
                        <input
                          type="radio"
                          id="staging-env"
                          name="environment"
                          value="staging"
                          checked={environment === 'staging'}
                          onChange={(e) => setEnvironment(e.target.value)}
                        />
                        <label htmlFor="staging-env" className="environment-label">
                          <div className="env-icon staging-icon">
                            <i className="fas fa-flask"></i>
                          </div>
                          <div className="env-info">
                            <h4>Staging</h4>
                            <p>Test your changes</p>
                            <small>URL: staging-{subdomain || 'yoursite'}.twilf.site</small>
                          </div>
                        </label>
                      </div>
                      
                      <div className="environment-option">
                        <input
                          type="radio"
                          id="production-env"
                          name="environment"
                          value="production"
                          checked={environment === 'production'}
                          onChange={(e) => setEnvironment(e.target.value)}
                        />
                        <label htmlFor="production-env" className="environment-label">
                          <div className="env-icon production-icon">
                            <i className="fas fa-globe"></i>
                          </div>
                          <div className="env-info">
                            <h4>Production</h4>
                            <p>Live website for your visitors</p>
                            <small>URL: {subdomain || 'yoursite'}.twilf.site</small>
                          </div>
                        </label>
                      </div>
                    </div>
                    
                    <div className="environment-help">
                      <small>
                        <i className="fas fa-info-circle"></i>
                        {environment === 'staging' 
                          ? 'Deploy to staging first to test your changes, then promote to production when ready.'
                          : 'Deploy directly to production for immediate public access.'
                                                }                      </small>                    </div>                  </div>                  {/* Custom Domain Option */}                  <div className="custom-domain-section">                    <div className="custom-domain-toggle">                      <input                        type="checkbox"                        id="use-custom-domain"                        checked={useCustomDomain}                        onChange={(e) => setUseCustomDomain(e.target.checked)}                      />                      <label htmlFor="use-custom-domain">                        I want to use my own domain                      </label>                    </div>                    
                    {/* DNS Provider Warning */}
                    {useCustomDomain && (
                      <div className="dns-provider-warning">
                        <div className="warning-content">
                          <i className="fas fa-exclamation-triangle"></i>
                          <div>
                            <strong>Important:</strong> Some DNS providers (like GoDaddy) don't support CNAME records for root domains (@). 
                            <a 
                              href={`/dns-instructions/${siteId}`}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="warning-link"
                            >
                              View solutions
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {useCustomDomain && (                      <div className="custom-domain-input">                        <label htmlFor="custom-domain">Your domain:</label>                        <div className="domain-input-group">                          <input                            type="text"                            id="custom-domain"                            value={customDomain}                            onChange={handleCustomDomainChange}                            placeholder="example.com"                            className={`domain-input ${domainValidation ? domainValidation : ''}`}                          />                          <div className="domain-validation-indicator">                            {getDomainValidationIcon()}                          </div>                        </div>                        {domainValidation && (                          <div className={`domain-validation-message ${domainValidation}`}>                            {getDomainValidationMessage()}                          </div>                        )}                        
                        {/* DNS Instructions Preview */}
                        {domainValidation === 'valid' && customDomain && (
                          <div className="dns-preview">
                            <h4>
                              <i className="fas fa-info-circle"></i>
                              DNS Setup Required
                            </h4>
                            <p>After publishing, you'll need to add these DNS records to your domain provider:</p>
                            
                            <div className="dns-preview-records">
                              <div className="dns-preview-record">
                                <div className="record-preview-info">
                                  <div className="record-preview-field">
                                    <label>Type:</label>
                                    <span>CNAME</span>
                                  </div>
                                  <div className="record-preview-field">
                                    <label>Name:</label>
                                    <span>@ (or leave blank)</span>
                                  </div>
                                  <div className="record-preview-field">
                                    <label>Points to:</label>
                                    <span>Will be provided after publishing</span>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="dns-preview-record">
                                <div className="record-preview-info">
                                  <div className="record-preview-field">
                                    <label>Type:</label>
                                    <span>CNAME</span>
                                  </div>
                                  <div className="record-preview-field">
                                    <label>Name:</label>
                                    <span>www</span>
                                  </div>
                                  <div className="record-preview-field">
                                    <label>Points to:</label>
                                    <span>Will be provided after publishing</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="dns-preview-help">
                              <div className="help-item">
                                <i className="fas fa-clock"></i>
                                <span>DNS changes can take up to 24 hours to take effect</span>
                              </div>
                              <div className="help-item">
                                <i className="fas fa-globe"></i>
                                <span>Your site will be accessible via Cloudflare URL immediately</span>
                              </div>
                              <div className="help-item">
                                <i className="fas fa-tools"></i>
                                <span>We'll provide exact DNS values after publishing</span>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="custom-domain-help">
                          <small>
                            <i className="fas fa-info-circle"></i>
                            You'll need access to your domain's DNS settings to complete setup.
                          </small>
                        </div>
                      </div>                    )}                  </div>
                </div>
              </div>

              {/* Publish Button */}
              <div className="publish-actions">
                <button
                  className="publish-btn"
                  onClick={handlePublish}
                  disabled={!canPublish() || isPublishing}
                >
                  {isPublishing ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Publishing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-rocket"></i>
                      Deploy to {environment.charAt(0).toUpperCase() + environment.slice(1)}
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>

        {publishStep === 'publishing' && (
          <div className="publishing-status">
            <div className="publishing-animation">
              <i className="fas fa-rocket fa-3x"></i>
            </div>
            <h3>Publishing your website...</h3>
            <p>This may take a few moments</p>
            <div className="progress-bar">
              <div className="progress-fill"></div>
            </div>
          </div>
        )}

        {publishStep === 'success' && (
          <div className="publish-success">
            <div className="success-icon">
              <i className="fas fa-check-circle fa-3x"></i>
            </div>
            <h3>Website Published Successfully!</h3>
            
            {publishResult?.cloudflareUrl && (
              <div className="published-urls">
                <div className="url-item">
                  <label>Live Website:</label>
                  <div className="url-display">
                    <span>{publishResult.cloudflareUrl}</span>
                    <button
                      className="copy-btn"
                      onClick={() => navigator.clipboard.writeText(publishResult.cloudflareUrl)}
                      title="Copy URL"
                    >
                      <i className="fas fa-copy"></i>
                    </button>
                    <a
                      href={publishResult.cloudflareUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="visit-btn"
                      title="Visit Website"
                    >
                      <i className="fas fa-external-link-alt"></i>
                    </a>
                  </div>
                </div>
              </div>
            )}

            {publishResult?.previewUrl && (
              <div className="url-item">
                <label>Preview URL:</label>
                <div className="url-display">
                  <span>{publishResult.previewUrl}</span>
                  <button
                    className="copy-btn"
                    onClick={() => navigator.clipboard.writeText(publishResult.previewUrl)}
                    title="Copy URL"
                  >
                    <i className="fas fa-copy"></i>
                  </button>
                  <a
                    href={publishResult.previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="visit-btn"
                    title="Visit Preview"
                  >
                    <i className="fas fa-external-link-alt"></i>
                  </a>
                </div>
              </div>
            )}

            {/* DNS Instructions for Custom Domain */}
            {publishResult?.dnsRecords && publishResult.dnsRecords.length > 0 && (
              <div className="dns-instructions">
                <h4>
                  <i className="fas fa-cog"></i>
                  DNS Configuration Required
                </h4>
                <p>To complete your custom domain setup, add these DNS records to your domain provider:</p>
                
                <div className="dns-records">
                  {publishResult.dnsRecords.map((record, index) => (
                    <div key={index} className="dns-record">
                      <div className="record-info">
                        <div className="record-field">
                          <label>Type:</label>
                          <span className="record-value">{record.type}</span>
                        </div>
                        <div className="record-field">
                          <label>Name:</label>
                          <span className="record-value">{record.name}</span>
                        </div>
                        <div className="record-field">
                          <label>Value:</label>
                          <span className="record-value">{record.target}</span>
                        </div>
                      </div>
                      <button
                        className="copy-record-btn"
                        onClick={() => navigator.clipboard.writeText(`${record.type} ${record.name} ${record.target}`)}
                        title="Copy DNS Record"
                      >
                        <i className="fas fa-copy"></i>
                      </button>
                    </div>
                  ))}
                </div>

                {/* Quick Access to Full DNS Instructions */}
                <div className="dns-instructions-link">
                  <div className="link-info">
                    <i className="fas fa-external-link-alt"></i>
                    <div>
                      <strong>Need detailed setup instructions?</strong>
                      <p>View comprehensive DNS setup guide with step-by-step instructions for all major domain providers.</p>
                    </div>
                  </div>
                  <a
                    href={`/dns-instructions/${siteId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="dns-guide-btn"
                  >
                    <i className="fas fa-book"></i>
                    View DNS Guide
                  </a>
                </div>

                {/* Step-by-step Instructions */}
                <div className="dns-setup-guide">
                  <h5>
                    <i className="fas fa-list-ol"></i>
                    Step-by-Step Setup Instructions
                  </h5>
                  
                  <div className="setup-steps">
                    <div className="setup-step">
                      <div className="step-number">1</div>
                      <div className="step-content">
                        <h6>Access Your Domain Provider</h6>
                        <p>Log in to your domain registrar's control panel (GoDaddy, Namecheap, Cloudflare, etc.)</p>
                      </div>
                    </div>
                    
                    <div className="setup-step">
                      <div className="step-number">2</div>
                      <div className="step-content">
                        <h6>Navigate to DNS Settings</h6>
                        <p>Look for "DNS Management", "DNS Records", or "Advanced DNS" in your control panel</p>
                      </div>
                    </div>
                    
                    <div className="setup-step">
                      <div className="step-number">3</div>
                      <div className="step-content">
                        <h6>Add DNS Records</h6>
                        <p>Add the CNAME records shown above. Delete any existing A records for @ and www if present.</p>
                      </div>
                    </div>
                    
                    <div className="setup-step">
                      <div className="step-number">4</div>
                      <div className="step-content">
                        <h6>Save Changes</h6>
                        <p>Save your DNS settings and wait for propagation (up to 24 hours)</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Common Providers Guide */}
                <div className="provider-guides">
                  <h5>
                    <i className="fas fa-question-circle"></i>
                    Common Domain Providers
                  </h5>
                  
                  <div className="provider-tabs">
                    <div className="provider-guide">
                      <h6><i className="fas fa-external-link-alt"></i> GoDaddy</h6>
                      <p>Go to "My Products" → "DNS" → "Manage Zones" → Add CNAME records</p>
                    </div>
                    
                    <div className="provider-guide">
                      <h6><i className="fas fa-external-link-alt"></i> Namecheap</h6>
                      <p>Go to "Domain List" → "Manage" → "Advanced DNS" → Add CNAME records</p>
                    </div>
                    
                    <div className="provider-guide">
                      <h6><i className="fas fa-external-link-alt"></i> Cloudflare</h6>
                      <p>Go to "DNS" → "Records" → Add CNAME records (turn off proxy for initial setup)</p>
                    </div>
                  </div>
                </div>

                {/* Troubleshooting */}
                <div className="dns-troubleshooting">
                  <h5>
                    <i className="fas fa-tools"></i>
                    Troubleshooting
                  </h5>
                  
                  <div className="troubleshooting-items">
                    <div className="troubleshooting-item">
                      <i className="fas fa-exclamation-triangle"></i>
                      <div>
                        <strong>Domain not working after 24 hours?</strong>
                        <p>Check that you've added both @ and www CNAME records correctly</p>
                      </div>
                    </div>
                    
                    <div className="troubleshooting-item">
                      <i className="fas fa-search"></i>
                      <div>
                        <strong>Check DNS propagation:</strong>
                        <p>Use tools like <a href="https://whatsmydns.net" target="_blank" rel="noopener noreferrer">whatsmydns.net</a> to check if your DNS changes have propagated</p>
                      </div>
                    </div>
                    
                    <div className="troubleshooting-item">
                      <i className="fas fa-times-circle"></i>
                      <div>
                        <strong>Remove conflicting records:</strong>
                        <p>Delete any existing A records for @ and www before adding CNAME records</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="dns-help">
                  <div className="help-item">
                    <i className="fas fa-info-circle"></i>
                    <span>DNS changes can take up to 24 hours to propagate worldwide.</span>
                  </div>
                  <div className="help-item">
                    <i className="fas fa-clock"></i>
                    <span>Your site will be accessible via the Cloudflare URL immediately.</span>
                  </div>
                  <div className="help-item">
                    <i className="fas fa-shield-alt"></i>
                    <span>SSL certificate will be automatically provisioned once DNS is configured.</span>
                  </div>
                </div>
              </div>
            )}

            <div className="success-actions">
              <button className="secondary-btn" onClick={onClose}>
                Close
              </button>
              {publishResult?.cloudflareUrl && (
                <a
                  href={publishResult.cloudflareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="primary-btn"
                >
                  <i className="fas fa-external-link-alt"></i>
                  Visit Website
                </a>
              )}
            </div>
          </div>
        )}

        {publishStep === 'error' && (
          <div className="publish-error">
            <div className="error-icon">
              <i className="fas fa-exclamation-triangle fa-3x"></i>
            </div>
            <h3>Publishing Failed</h3>
            <p>{publishResult?.error || 'An unknown error occurred'}</p>
            
            <div className="error-actions">
              <button className="secondary-btn" onClick={() => setPublishStep('configure')}>
                Try Again
              </button>
              <button className="primary-btn" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublishModal; 